<template>
  <div class="judge-grid">
    <div class="judge-card">
      <p class="judge-title">{{ $t('judge.responsible_judge') }}</p>
      <a v-if="judge && judge.id" href="#" class="judge-link" @click.prevent="emitProfile(judge.id)">{{ judgeName }}</a>
      <span v-else class="judge-empty">{{ judgeName }}</span>
      <p v-if="scoringDuration" class="judge-meta">
        {{ $t('judge.scoring_time') || 'Tiempo de puntuación' }}: <strong>{{ scoringDuration }}</strong>
      </p>
    </div>

    <div class="judge-card">
      <p class="judge-title">{{ $t('judge.editors') || 'Editores' }}</p>
      <template v-if="hasEditors">
        <div class="judge-editors">
          <div
            v-for="editor in editors"
            :key="editorKey(editor)"
            class="editor-item"
          >
            <button
              type="button"
              class="judge-link muted editor-trigger"
              @click.prevent="toggleEditorMenu(editor)"
            >
              {{ editor.name }} {{ editor.surname }}
            </button>

            <div v-if="isEditorOpen(editor)" class="editor-menu">
              <div class="editor-menu__card">
                <div class="editor-menu__header">
                  <div class="editor-menu__title">{{ editor.name }} {{ editor.surname }}</div>
                  <button type="button" class="editor-menu__close" @click.stop="closeMenu">×</button>
                </div>

                <div v-if="editorChanges(editor).length" class="editor-menu__list">
                  <div
                    v-for="diff in editorChanges(editor)"
                    :key="diffKey(diff)"
                    class="editor-change"
                  >
                    <div class="editor-change__row">
                      <span class="editor-change__status" :class="diffPillClass(diff)">
                        <i :class="statusIcon(diff)"></i>
                      </span>
                      <div class="editor-change__body">
                        <div
                          v-if="!dubChangeLines(diff).length"
                          class="editor-change__title"
                        >
                          {{ trickTitle(diff) }}
                        </div>
                        <div
                          v-if="changeSummary(diff) || grabChip(diff) || dubChip(diff) || dubChangeLines(diff).length"
                          class="editor-change__summary"
                          :class="{ 'is-multiline': dubChangeLines(diff).length }"
                        >
                          <template v-if="dubChangeLines(diff).length">
                            <div
                              v-for="line in dubChangeLines(diff)"
                              :key="line.key"
                              class="dub-line"
                            >
                              <span class="dub-line__name" :class="{ 'is-after': line.role === 'after' }">{{ line.name }}</span>
                              <span v-if="line.dub" class="chip chip-dub chip-inline chip-block">{{ line.dub }}</span>
                            </div>
                          </template>
                          <template v-else>
                            <span v-if="changeSummary(diff)">{{ changeSummary(diff) }}</span>
                            <span v-if="grabChip(diff)" class="chip chip-grab chip-inline">{{ grabChip(diff) }}</span>
                            <span v-if="dubChip(diff)" class="chip chip-dub chip-inline">{{ dubChip(diff) }}</span>
                          </template>
                        </div>
                      </div>
                      <div class="editor-change__connector" v-if="formatDateTimeShort(diff.edited_at || diff.updated_at)">
                        <span class="editor-change__arrow"><i class="fas fa-arrow-turn-down"></i></span>
                        <span class="editor-change__stamp">{{ formatDateTimeShort(diff.edited_at || diff.updated_at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="editor-menu__empty">
                  {{ $t('judge.no_changes_editor') || 'Sin cambios de este editor' }}
                </div>

              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <span class="judge-empty">{{ $t('judge.no_editors') || 'Sin editores' }}</span>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JudgeInfo',
  props: {
    judge: {
      type: Object,
      default: () => ({}),
    },
    editors: {
      type: Array,
      default: () => [],
    },
    scoringDuration: {
      type: String,
      default: null,
    },
    diffs: {
      type: Array,
      default: () => [],
    },
    externalOpenEvent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      openEditorId: null,
      grabOptions: [
        { id: 1, name: 'Blunt' },
        { id: 2, name: 'Boneless' },
        { id: 3, name: 'BuddhaGrab' },
        { id: 4, name: 'CanOpener' },
        { id: 5, name: 'ChristAir' },
        { id: 6, name: 'Crossover' },
        { id: 7, name: 'CrossoverSafety' },
        { id: 8, name: 'CrossoverTruckDriver' },
        { id: 9, name: 'Flat' },
        { id: 10, name: 'HalfSquirrelLef.' },
        { id: 11, name: 'HalfSquirrelRig.' },
        { id: 12, name: 'HighChair' },
        { id: 13, name: 'HorizontalTwist' },
        { id: 14, name: 'Japan' },
        { id: 15, name: 'KneelAir' },
        { id: 16, name: 'Layout' },
        { id: 17, name: 'Mute' },
        { id: 18, name: 'No grab' },
        { id: 19, name: 'Open' },
        { id: 20, name: 'Pike' },
        { id: 21, name: 'SafetyGrab' },
        { id: 22, name: 'Scratch' },
        { id: 23, name: 'Seatbelt' },
        { id: 24, name: 'Shifty' },
        { id: 25, name: 'SpreadEagle' },
        { id: 26, name: 'SquirrelGrab' },
        { id: 27, name: 'Superman' },
        { id: 28, name: 'TruckDriver' },
        { id: 29, name: 'Tuck' },
        { id: 30, name: 'TuckBlunt' },
        { id: 31, name: 'TuckedTwist' },
        { id: 32, name: 'Twist' },
        { id: 33, name: 'Yokohama' },
      ],
    };
  },
  computed: {
    judgeName() {
      const first = this.judge?.name || '';
      const last = this.judge?.surname || '';
      const fullName = `${first} ${last}`.trim();
      return fullName || (this.$t('judge.not_available') || 'No disponible');
    },
    hasEditors() {
      return Array.isArray(this.editors) && this.editors.length > 0;
    },
    groupedDiffs() {
      if (!Array.isArray(this.diffs)) return {};
      return this.diffs.reduce((acc, diff) => {
        const editorId = this.getEditorId(diff?.editor);
        if (!editorId) return acc;
        if (!acc[editorId]) acc[editorId] = [];
        acc[editorId].push(diff);
        return acc;
      }, {});
    },
  },
  methods: {
    editorKey(editor) {
      return this.getEditorId(editor) || `${editor.name || 'editor'}-${editor.surname || ''}`;
    },
    getEditorId(editor) {
      if (!editor) return null;
      return editor.judge_id
        || editor.id
        || editor.user_id
        || [editor.name, editor.surname].filter(Boolean).join(' ').trim()
        || null;
    },
    isEditorOpen(editor) {
      return this.openEditorId && this.openEditorId === this.getEditorId(editor);
    },
    toggleEditorMenu(editor) {
      const id = this.getEditorId(editor);
      if (!id) return;
      this.openEditorId = this.openEditorId === id ? null : id;
    },
    closeMenu() {
      this.openEditorId = null;
    },
    editorChanges(editor) {
      const id = this.getEditorId(editor);
      const items = id ? (this.groupedDiffs[id] || []) : [];
      return [...items].sort((a, b) => this.diffTimestamp(b) - this.diffTimestamp(a));
    },
    diffKey(diff) {
      const ts = this.diffTimestamp(diff);
      const idx = diff?.index ?? diff?.trick_index ?? 'idx';
      const editorId = this.getEditorId(diff?.editor) || 'editor';
      return diff?.id || `${editorId}-${idx}-${ts}`;
    },
    variationLabel(value) {
      const v = Number(value);
      if (v === 1) return 'FS';
      if (v === 2) return 'BS';
      if (!Number.isFinite(v) || v <= 0) return null;
      if (Number.isNaN(v)) return null;
      return `Var ${v}`;
    },
    grabLabel(value) {
      const id = Number(value);
      if (!Number.isFinite(id)) return null;
      const found = this.grabOptions.find((g) => Number(g.id) === id);
      return found ? found.name : null;
    },
    getGrabValue(diff, side) {
      if (!diff) return null;
      const sideKey = side === 'before' ? 'before' : 'after';
      const nested = diff?.[sideKey] || {};
      const nestedVal = nested.grab ?? nested.grab_id;
      if (nestedVal !== undefined && nestedVal !== null) return nestedVal;
      const topVal = diff?.[`grab_${sideKey}`];
      return topVal !== undefined ? topVal : null;
    },
    getDubValue(diff, side) {
      if (!diff) return null;
      const sideKey = side === 'before' ? 'before' : 'after';
      const nested = diff?.[sideKey] || {};
      const nestedVal = nested.dub ?? nested.dub_id;
      if (nestedVal !== undefined && nestedVal !== null) return nestedVal;
      const topVal = diff?.[`dub_${sideKey}`];
      return topVal !== undefined ? topVal : null;
    },
    dubLabel(value) {
      const v = Number(value);
      if (!Number.isFinite(v)) return null;
      if (v === 1) return '20%';
      if (v === 2) return '50%';
      if (v === 3) return '80%';
      return null;
    },
    buildLine(diff, side, { includeExtras = true } = {}) {
      const source = side === 'after' ? (diff?.after || {}) : (diff?.before || {});
      const name = this.extractTrickName(source)
        || this.extractTrickName(diff?.before)
        || this.extractTrickName(diff?.after)
        || (this.$t('judge.trick') || 'Truco');
      const varLabel = this.variationLabel(source.variation || source.variation_id);

      const parts = [name];
      if (includeExtras) {
        if (varLabel) parts.push(varLabel);
      }

      return parts.join(' ');
    },
    grabChip(diff) {
      const grabAfter = this.grabLabel(this.getGrabValue(diff, 'after'));
      const grabBefore = this.grabLabel(this.getGrabValue(diff, 'before'));
      if (grabAfter) return grabAfter;
      if (grabBefore) return grabBefore;
      return null;
    },
    dubChip(diff) {
      const dubAfter = this.dubLabel(this.getDubValue(diff, 'after'));
      const dubBefore = this.dubLabel(this.getDubValue(diff, 'before'));
      if (dubAfter) return dubAfter;
      if (dubBefore) return dubBefore;
      return null;
    },
    dubChangeLines(diff) {
      const nameBefore = this.buildLine(diff, 'before', { includeExtras: true });
      const nameAfter = this.buildLine(diff, 'after', { includeExtras: true });
      const dubBefore = this.dubLabel(this.getDubValue(diff, 'before'));
      const dubAfter = this.dubLabel(this.getDubValue(diff, 'after'));

      // Only render when the dub value changes (including null transitions)
      if (dubAfter === dubBefore) return [];

      const beforeLine = {
        key: `before-${nameBefore}-${dubBefore || 'null'}`,
        name: nameBefore,
        dub: dubBefore || null,
        role: 'before',
      };
      const afterLine = {
        key: `after-${nameAfter}-${dubAfter || 'null'}`,
        name: nameAfter,
        dub: dubAfter || null,
        role: 'after',
      };

      return [beforeLine, afterLine].filter((line) => !!line.name);
    },
    changeSummary(diff) {
      const action = this.normalizedAction(diff);

      if (action === 'moved') {
        const afterIdx = this.getIndexValue(diff, 'after');
        const afterName = this.buildLine(diff, 'after', { includeExtras: true });
        const afterDisplay = Number.isInteger(afterIdx) ? afterIdx + 1 : null;
        return afterDisplay ? `${afterName} (pos ${afterDisplay})` : afterName;
      }

      const before = diff?.before || {};
      const after = diff?.after || {};
      const nameBefore = before.trick_name || before.name || null;
      const nameAfter = after.trick_name || after.name || null;
      const varBefore = this.variationLabel(before.variation || before.variation_id);
      const varAfter = this.variationLabel(after.variation || after.variation_id);
      const grabBefore = this.grabLabel(this.getGrabValue(diff, 'before'));
      const grabAfter = this.grabLabel(this.getGrabValue(diff, 'after'));
      const dubBefore = this.dubLabel(this.getDubValue(diff, 'before'));
      const dubAfter = this.dubLabel(this.getDubValue(diff, 'after'));

      const nameChanged = nameBefore && nameAfter && nameBefore !== nameAfter;
      const changed = nameChanged
        || (varBefore !== varAfter)
        || (grabBefore !== grabAfter)
        || (dubBefore !== dubAfter);

      if (!changed) return null;

      // For added/deleted, show only the chips/second row, no text duplication
      if (action === 'added' || action === 'deleted') return null;

      return this.buildLine(diff, 'after', { includeExtras: true });
    },
    diffTimestamp(diff) {
      const raw = diff?.edited_at || diff?.updated_at || diff?.created_at;
      const ts = Date.parse(raw || '');
      return Number.isFinite(ts) ? ts : 0;
    },
    extractTrickName(trick) {
      return trick?.trick_name || trick?.name || trick?.title || null;
    },
    getIndexValue(diff, side) {
      const source = side === 'before' ? diff?.before : diff?.after;
      const nestedIndex = source?.index ?? source?.trick_index;

      const explicitBefore = diff?.index_before ?? diff?.indexBefore ?? diff?.from_index ?? diff?.fromIndex;
      const explicitAfter = diff?.index_after ?? diff?.indexAfter ?? diff?.to_index ?? diff?.toIndex;
      const topIndex = diff?.index ?? diff?.trick_index;

      const mappedIndex = side === 'before' ? explicitBefore : explicitAfter;

      const candidate = [nestedIndex, mappedIndex, topIndex]
        .map((val) => (val === undefined || val === null ? null : Number(val)))
        .find((val) => Number.isInteger(val));

      return Number.isInteger(candidate) ? candidate : null;
    },
    trickTitle(diff) {
      const nameBefore = this.extractTrickName(diff?.before);
      const nameAfter = this.extractTrickName(diff?.after);
      const nameChanged = nameBefore && nameAfter && nameBefore !== nameAfter;
      const action = this.normalizedAction(diff);

      if (action === 'moved') {
        const beforeIdx = this.getIndexValue(diff, 'before');
        const beforeName = this.buildLine(diff, 'before', { includeExtras: true });
        const beforeDisplay = Number.isInteger(beforeIdx) ? beforeIdx + 1 : null;
        return beforeDisplay ? `${beforeName} (pos ${beforeDisplay})` : beforeName;
      }

      if (action === 'added') {
        return this.buildLine(diff, 'after', { includeExtras: true });
      }
      if (action === 'deleted') {
        return this.buildLine(diff, 'before', { includeExtras: true });
      }

      return this.buildLine(diff, 'before', { includeExtras: !nameChanged });
    },
    extractScore(trick) {
      if (!trick) return null;
      const value = trick.score ?? trick.total_score ?? null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    },
    formatScore(num) {
      return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    },
    scoreText(diff) {
      const before = this.extractScore(diff?.before);
      const after = this.extractScore(diff?.after);
      if (before !== null && after !== null && before !== after) {
        return `${this.formatScore(before)} -> ${this.formatScore(after)}`;
      }
      if (after !== null) return this.formatScore(after);
      if (before !== null) return this.formatScore(before);
      return null;
    },
    normalizedAction(diff) {
      const raw = (diff?.action || diff?.change_type || '').toLowerCase();
      if (raw === 'removed') return 'deleted';
      if (raw === 'edited_moved') return 'edited';
      if (raw === 'moved') return 'moved';
      return raw || 'edited';
    },
    actionLabel(diff) {
      const action = this.normalizedAction(diff);
      const addLabel = this.changeLabel('added');
      const editLabel = this.changeLabel('edited');
      const removeLabel = this.changeLabel('removed');
      if (action === 'added') return addLabel;
      if (action === 'deleted') return removeLabel;
      if (action === 'moved') return this.$t?.('judge.moved') || editLabel;
      return editLabel;
    },
    diffPillClass(diff) {
      const action = this.normalizedAction(diff);
      if (action === 'added') return 'is-added';
      if (action === 'deleted') return 'is-removed';
      if (action === 'moved') return 'is-moved';
      if (action === 'edited') return 'is-edited';
      return 'is-changed';
    },
    formatTimestamp(raw) {
      if (!raw) return '';
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    formatDateTimeShort(raw) {
      if (!raw) return '';
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return '';
      const d = date.toLocaleDateString([], { month: 'short', day: '2-digit' });
      const t = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `${d} ${t}`;
    },
    statusIcon(diff) {
      const action = this.normalizedAction(diff);
      if (action === 'added') return 'fas fa-plus';
      if (action === 'deleted') return 'fas fa-trash';
      if (action === 'moved') return 'fas fa-arrows-alt';
      return 'fas fa-pen';
    },
    changeLabel(action) {
      const keyMap = {
        added: 'judging_program.change_badge_added',
        edited: 'judging_program.change_badge_edited',
        removed: 'judging_program.change_badge_deleted',
        moved: 'judge.moved',
      };
      const key = keyMap[action] || keyMap.edited;
      const txt = this.$t ? this.$t(key) : '';
      if (txt && typeof txt === 'string') return txt;
      if (action === 'added') return 'Nuevo';
      if (action === 'removed') return 'Eliminado';
      if (action === 'moved') return 'Reordenado';
      return 'Editado';
    },
    emitProfile(id) {
      if (!id) return;
      this.$emit('view-profile', id);
    },
    handleOutsideClick(event) {
      if (!this.openEditorId) return;
      if (this.$el && !this.$el.contains(event.target)) {
        this.closeMenu();
      }
    },
  },
  watch: {
    editors: {
      handler(next) {
        if (!Array.isArray(next)) {
          this.openEditorId = null;
          return;
        }
        const stillExists = next.some((ed) => this.getEditorId(ed) === this.openEditorId);
        if (!stillExists) {
          this.openEditorId = null;
        }
      },
      deep: true,
    },
    externalOpenEvent(next) {
      if (!next) return;
      const targetId = this.getEditorId(next.editor || next)
        || next.editorId
        || next.editor_id
        || next.id
        || null;
      if (targetId) {
        this.openEditorId = targetId;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style scoped>
.judge-register {
  margin-top: 8px;
  text-align: center;
}

.judge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 8px;
}

.judge-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px 14px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

.judge-title {
  margin: 0 0 6px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #0f172a;
}

.judge-link {
  display: inline-block;
  cursor: pointer;
  color: #0ea5e9;
  font-weight: 600;
  transition: color 0.2s;
}

.judge-link.muted {
  color: #6b7280;
  font-weight: 500;
}

.judge-link:hover {
  color: #0284c7;
  text-decoration: underline;
}

.judge-link.muted:hover {
  color: #4b5563;
}

.judge-editors {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.editor-item {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.editor-trigger {
  background: transparent;
  border: none;
  padding: 0;
}

.editor-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: auto;
  z-index: 50;
}

.editor-menu__card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 12px 14px;
  min-width: 420px;
  max-width: 520px;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

.editor-menu__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.editor-menu__title {
  font-weight: 700;
  color: #0f172a;
}

.editor-menu__close {
  background: transparent;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
}

.editor-menu__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.editor-change {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
}

.editor-change__row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 10px;
  min-height: 36px;
}

.editor-change__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: #fff;
  font-size: 0.8rem;
}

.editor-change__status.is-added {
  background: #16a34a;
}

.editor-change__status.is-removed {
  background: #dc2626;
}

.editor-change__status.is-edited,
.editor-change__status.is-changed {
  background: #f59e0b;
  color: #1f2937;
}
.editor-change__status.is-moved {
  background: #0ea5e9;
}

.editor-change__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  align-items: flex-start;
}

.editor-change__title {
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editor-change__summary {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-wrap: wrap;
}

.editor-change__summary.is-multiline {
  flex-direction: column;
  align-items: flex-start;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.8rem;
  line-height: 1.2;
  border: 1px solid #d1d5db;
  background: #f8fafc;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-inline {
  margin-left: 4px;
}

.chip-block {
  margin-left: 0;
  width: fit-content;
}

.dub-line {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.dub-line__name {
  font-weight: 500;
  color: #0f172a;
}

.dub-line__name.is-after {
  font-weight: 700;
}

.chip-grab {
  background: rgba(250, 230, 41, 0.3);
  color: #5b4a00;
  border-color: #facc15;
}

.chip-dub {
  background: rgba(255, 96, 96, 0.2);
  color: #7f1d1d;
  border-color: #f87171;
}

.editor-change__connector {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: #6b7280;
  font-size: 0.75rem;
}

.editor-change__arrow {
  font-size: 0.85rem;
}

.editor-change__stamp {
  white-space: nowrap;
}

.editor-menu__empty {
  font-size: 0.9rem;
  color: #6b7280;
  padding: 6px 0;
}

.judge-empty {
  color: #9ca3af;
  font-size: 0.9rem;
}

.judge-meta {
  margin: 6px 0 0;
  font-size: 0.9rem;
  color: #1f2937;
}

@media (max-width: 540px) {
  .editor-menu__card {
    min-width: 100%;
    max-width: 100%;
    right: 0;
    left: 0;
  }

  .editor-change__row {
    grid-template-columns: auto 1fr auto;
    gap: 8px;
  }

  .editor-change__title,
  .editor-change__summary {
    white-space: normal;
  }
}
</style>
